// Tessera Full entitlement — multi-platform purchase flow.
//
// Three payment rails, picked at runtime via feature detection:
//   1. iOS Capacitor → RevenueCat (StoreKit 2 under the hood)
//   2. Android TWA → Digital Goods API (Play Billing)
//   3. Plain web / everything else → Stripe (not wired yet, returns 'unsupported')
//
// Each rail writes the same localStorage flag (`tessera.entitlement.v1`), so
// downstream code (App.tsx fullUnlock state) doesn't care which rail sold
// the unlock. Cross-device restore is handled per-rail: DGAPI remembers the
// user's Play account; RevenueCat tracks the Apple ID; Stripe would use
// Firestore for the web case.
//
// Client-side flow only — no server-side receipt validation in v1. For a
// $0.99 non-consumable on a small indie game, the economics of fraud
// prevention don't justify the ops cost. If piracy becomes material, the
// validator is already stubbed in functions/validatePurchase.ts; wire it
// then. For now the trust boundary is: the Play Billing Library signs
// purchases with Google's key, and DGAPI surfaces the Play-verified state.
// A tampering user would have to forge a Play-signed receipt, which is
// impractical at this price point.
//
// Entitlement is persisted to localStorage immediately after a successful
// purchase so the unlock survives a refresh. On subsequent launches we
// ALSO re-query DGAPI to catch cross-device unlocks (same Google account
// = same entitlement), so uninstall/reinstall or fresh device both recover
// the purchase without user action.

const SKU = 'tessera_full_unlock';
const LOCAL_KEY = 'tessera.entitlement.v1';

// RevenueCat "entitlement identifier" — a logical grouping of one or more
// products that grant access. For Tessera there's a single entitlement
// 'Full' that's unlocked by the `tessera_full_unlock` product on iOS
// (SKU is the same on both platforms for consistency with Play). Configure
// in RevenueCat dashboard: Entitlements → Create → 'Full' → attach product.
// Identifier is CASE-SENSITIVE — code and dashboard must match exactly.
const RC_ENTITLEMENT = 'Full';

export type PurchaseOutcome = 'success' | 'cancelled' | 'failed' | 'unsupported';

export interface PurchaseResult {
  outcome: PurchaseOutcome;
  // Populated when the Play Billing call threw or when DGAPI isn't
  // available. Surfaced in the paywall diagnostic strip so users can see
  // the specific failure (e.g. "Item unavailable" while Play propagates
  // a freshly-activated SKU). Not shown to end users in production.
  error?: string;
}

interface DigitalGoodsService {
  getDetails(itemIds: string[]): Promise<ItemDetails[]>;
  listPurchases(): Promise<PurchaseDetails[]>;
  // Some Chrome revisions expose consume/acknowledge; others fold it into
  // the PaymentRequest completion. We don't rely on either — listPurchases
  // is the source of truth.
}
interface ItemDetails {
  itemId: string;
  title?: string;
  description?: string;
  price?: { currency: string; value: string };
}
interface PurchaseDetails {
  itemId: string;
  purchaseToken: string;
}

// Minimal local-store layer. Keep the shape open-ended so future SKUs (a
// hypothetical cosmetic unlock, etc.) can coexist without migration.
interface StoredEntitlement {
  fullUnlock?: boolean;
  purchasedAt?: number; // epoch ms, for analytics / receipts only
}

function readLocal(): StoredEntitlement {
  try {
    const raw = localStorage.getItem(LOCAL_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as StoredEntitlement;
    return parsed ?? {};
  } catch {
    return {};
  }
}

function writeLocal(e: StoredEntitlement) {
  try {
    localStorage.setItem(LOCAL_KEY, JSON.stringify(e));
  } catch { /* storage full / disabled / incognito — fine, in-memory state still works */ }
}

export function hasFullUnlockLocal(): boolean {
  return readLocal().fullUnlock === true;
}

function setFullUnlockLocal() {
  writeLocal({ fullUnlock: true, purchasedAt: Date.now() });
}

// Platform detection. Runs once; result cached to avoid recomputing on each
// purchase. Detects iOS Capacitor (RevenueCat rail), plain web (falls
// through to DGAPI rail for Android TWA or 'unsupported' elsewhere).
//
// Exported because the paywall UI needs to render iOS- vs Android-specific
// copy (e.g. "App Store" vs "Play Billing"). Same async dynamic-import
// pattern used internally — the platform value never flips after first
// detection so callers can cache the result freely.
let _platform: 'ios-capacitor' | 'web' | null = null;
export async function detectPlatform(): Promise<'ios-capacitor' | 'web'> {
  if (_platform !== null) return _platform;
  try {
    const { Capacitor } = await import('@capacitor/core');
    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
      _platform = 'ios-capacitor';
      return _platform;
    }
  } catch {
    // @capacitor/core absent (SSR / older test runners) — fine, just
    // means we're not in a Capacitor context.
  }
  _platform = 'web';
  return _platform;
}

// Lazy-initialize the RevenueCat SDK. Uses dynamic import so the ~50KB of
// Purchases code is tree-shaken out of the Android-TWA bundle (which uses
// DGAPI instead). Caller must provide the RevenueCat iOS public API key
// via VITE_REVENUECAT_IOS_API_KEY env var (set at build time in CI or
// .env.local). Without the key, the iOS rail returns 'unsupported' and
// the user sees "In-app purchase isn't available" on the paywall.
let _rcInitialized = false;
async function ensureRevenueCatConfigured(): Promise<boolean> {
  if (_rcInitialized) return true;
  const apiKey = (import.meta as unknown as { env: Record<string, string | undefined> }).env
    ?.VITE_REVENUECAT_IOS_API_KEY;
  if (!apiKey) {
    console.warn('[entitlements] VITE_REVENUECAT_IOS_API_KEY not set — iOS IAP disabled');
    return false;
  }
  try {
    const { Purchases, LOG_LEVEL } = await import('@revenuecat/purchases-capacitor');
    await Purchases.setLogLevel({ level: LOG_LEVEL.WARN });
    await Purchases.configure({ apiKey });
    _rcInitialized = true;
    return true;
  } catch (e) {
    console.warn('[entitlements] RevenueCat configure failed', e);
    return false;
  }
}

// ---------- iOS (RevenueCat) rail ----------

async function iosQueryOwnership(): Promise<boolean> {
  if (!(await ensureRevenueCatConfigured())) return false;
  try {
    const { Purchases } = await import('@revenuecat/purchases-capacitor');
    const info = await Purchases.getCustomerInfo();
    const active = info.customerInfo.entitlements.active;
    return Object.prototype.hasOwnProperty.call(active, RC_ENTITLEMENT);
  } catch (e) {
    console.warn('[entitlements] iOS getCustomerInfo failed', e);
    return false;
  }
}

async function iosPurchase(): Promise<PurchaseResult> {
  if (!(await ensureRevenueCatConfigured())) {
    return { outcome: 'unsupported', error: 'RevenueCat not configured' };
  }
  try {
    const { Purchases, PURCHASES_ERROR_CODE } = await import('@revenuecat/purchases-capacitor');
    // Fetch the product from the currently-configured offering. Tessera
    // uses a single offering with a single package containing
    // tessera_full_unlock.
    const offerings = await Purchases.getOfferings();
    const currentOffering = offerings.current;
    if (!currentOffering) {
      return { outcome: 'failed', error: 'No RevenueCat current offering configured' };
    }
    // Pick the package that grants our 'full' entitlement. For a single-
    // product offering this is always the one-and-only package.
    const pkg = currentOffering.availablePackages[0];
    if (!pkg) {
      return { outcome: 'failed', error: 'Offering has no available packages' };
    }
    const { customerInfo } = await Purchases.purchasePackage({ aPackage: pkg });
    const owned = Object.prototype.hasOwnProperty.call(
      customerInfo.entitlements.active,
      RC_ENTITLEMENT,
    );
    if (owned) {
      setFullUnlockLocal();
      return { outcome: 'success' };
    }
    return { outcome: 'failed', error: 'Purchase completed but entitlement not granted' };
  } catch (e) {
    const err = e as { code?: string | number; message?: string };
    // RevenueCat's cancelled-by-user error — specific code, map to our
    // 'cancelled' outcome so the paywall shows "Your progress is still saved."
    if (err?.code === 'PURCHASE_CANCELLED_ERROR' || err?.code === 1) {
      return { outcome: 'cancelled' };
    }
    const msg = err?.message ?? String(e);
    console.warn('[entitlements] iOS purchase failed', e);
    return { outcome: 'failed', error: `iOS: ${msg}` };
  }
}

// ---------- Android TWA (Digital Goods API) rail ----------

// Feature detection. Must run on a real page — Node/SSR will blow up without
// the try/catch. DGAPI requires both the navigator entry AND the Play Billing
// payment method being installable; we treat either absence as 'not
// available' and let callers fall back.
function getService(): Promise<DigitalGoodsService | null> {
  try {
    const getter = (window as unknown as {
      getDigitalGoodsService?: (id: string) => Promise<DigitalGoodsService | null>;
    }).getDigitalGoodsService;
    if (typeof getter !== 'function') return Promise.resolve(null);
    return getter('https://play.google.com/billing').catch(() => null);
  } catch {
    return Promise.resolve(null);
  }
}

export async function isDigitalGoodsAvailable(): Promise<boolean> {
  const svc = await getService();
  return svc !== null;
}

// Platform-aware availability — true if ANY purchase rail is wired on the
// current runtime. Used by the paywall to decide whether to enable the
// Unlock button vs. show an "iap unavailable" message.
export async function isPurchaseAvailable(): Promise<boolean> {
  const platform = await detectPlatform();
  if (platform === 'ios-capacitor') {
    return ensureRevenueCatConfigured();
  }
  return isDigitalGoodsAvailable();
}

// Polls the active purchase rail for ownership. Call this on every app
// boot — it lights up the unlock on a fresh device as soon as the user's
// platform account matches. Returns the cumulative truth: local flag OR
// platform confirms ownership.
export async function refreshEntitlementFromPlay(): Promise<boolean> {
  const local = hasFullUnlockLocal();
  const platform = await detectPlatform();
  if (platform === 'ios-capacitor') {
    const owned = await iosQueryOwnership();
    if (owned && !local) setFullUnlockLocal();
    return owned || local;
  }
  // Android TWA / web — DGAPI path.
  const svc = await getService();
  if (!svc) return local;
  try {
    const purchases = await svc.listPurchases();
    const owned = purchases.some((p) => p.itemId === SKU);
    if (owned && !local) setFullUnlockLocal();
    return owned || local;
  } catch (e) {
    console.warn('[entitlements] listPurchases failed', e);
    return local;
  }
}

// Initiate the purchase. Opens Play Billing's full-screen confirm UI. The
// user may cancel, the network may fail, or the account may be restricted —
// we disambiguate the outcomes so the paywall can render appropriate copy.
export async function purchaseFullUnlock(): Promise<PurchaseResult> {
  // Platform-routed: iOS uses RevenueCat, everyone else tries DGAPI.
  const platform = await detectPlatform();
  if (platform === 'ios-capacitor') return iosPurchase();

  const svc = await getService();
  if (!svc) return { outcome: 'unsupported', error: 'getDigitalGoodsService returned null' };
  if (typeof PaymentRequest === 'undefined') {
    return { outcome: 'unsupported', error: 'PaymentRequest not defined' };
  }

  let stage = 'init';
  try {
    stage = 'listPurchases-pre';
    // Double-check ownership before charging — prevents double-purchase if
    // the user taps paywall button after restoring from Play but before the
    // local flag was set (race on cold start).
    const existing = await svc.listPurchases();
    if (existing.some((p) => p.itemId === SKU)) {
      setFullUnlockLocal();
      return { outcome: 'success' };
    }

    stage = 'paymentRequest-new';
    const request = new PaymentRequest(
      [{
        supportedMethods: 'https://play.google.com/billing',
        data: { sku: SKU },
      }],
      {
        total: {
          label: 'Tessera Full',
          amount: { currency: 'USD', value: '0.99' },
        },
      },
    );

    stage = 'paymentRequest-show';
    const response = await request.show();

    stage = 'paymentRequest-complete';
    await response.complete('success');

    stage = 'listPurchases-post';
    for (let attempt = 0; attempt < 2; attempt++) {
      const latest = await svc.listPurchases();
      if (latest.some((p) => p.itemId === SKU)) {
        setFullUnlockLocal();
        return { outcome: 'success' };
      }
      if (attempt === 0) await new Promise((r) => setTimeout(r, 400));
    }
    setFullUnlockLocal();
    return { outcome: 'success' };
  } catch (e) {
    const err = e as { name?: string; message?: string };
    if (err?.name === 'AbortError') {
      return { outcome: 'cancelled', error: `stage=${stage}` };
    }
    const name = err?.name ?? 'Error';
    const msg = err?.message ?? String(e);
    console.warn('[entitlements] purchase failed', stage, e);
    return { outcome: 'failed', error: `${name} @ ${stage}: ${msg}` };
  }
}

// NOTE: the closed-testing "tester code" unlock (applyTesterCodeUnlock +
// TESSERA-OPEN) was REMOVED in v0.18.3 (2026-06-08) ahead of the Google Play
// production launch. It let testers unlock the paid content without going
// through Play Billing during the Closed Testing window; leaving it in
// production would defeat the paywall for anyone reading the bundle. Existing
// testers who already unlocked keep their entitlement (it lives in the
// `tessera.entitlement.v1` localStorage key, untouched by this removal).

// Called once on app startup. Kicks off the DGAPI refresh in the background
// and returns the cached-local answer synchronously to the caller so UI
// doesn't flash unowned → owned. The async update then fires a listener if
// the state actually flipped.
export function initEntitlement(onChange: (fullUnlock: boolean) => void): boolean {
  const initial = hasFullUnlockLocal();
  // Kick off refresh in the background — result is reported via onChange.
  void refreshEntitlementFromPlay().then((now) => {
    if (now !== initial) onChange(now);
  });
  return initial;
}
